import { z } from "zod";

export const SLUG_REGEX = /^[a-z0-9-]+$/;
export const HEX_REGEX = /^#([0-9a-fA-F]{3}){1,2}$/;
export const DOMAIN_REGEX =
  /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;

const optionalEmail = z
  .string()
  .trim()
  .email("Invalid email")
  .optional()
  .or(z.literal(""));

const optionalHex = z
  .string()
  .trim()
  .regex(HEX_REGEX, "Use a valid hex like #0d5eaf")
  .optional()
  .or(z.literal(""));

// Shared base — used by both create and edit. Keys here MUST match the
// backend API contract (see core-lambda/admin_module/corporate_management/
// corporate/schemas.py: CreateCorporateRequest / UpdateCorporateRequest).
const baseShape = {
  name: z.string().trim().min(1, "Name is required").max(200),
  display_name: z.string().trim().max(200).optional().or(z.literal("")),
  verification_email_domains: z
    .array(z.object({ value: z.string().trim() }))
    .optional()
    .default([]),
  primary_color: optionalHex,
  secondary_color: optionalHex,
  industry: z.string().trim().max(100).optional().or(z.literal("")),
  timezone: z.string().trim().max(64).optional().or(z.literal("")),
  support_email: optionalEmail,
  support_phone: z.string().trim().max(32).optional().or(z.literal("")),
  account_owner_email: optionalEmail,
  contract_seat_limit: z
    .union([z.coerce.number().int().min(0), z.literal("")])
    .optional(),
  contract_start_date: z.string().optional().or(z.literal("")),
  contract_end_date: z.string().optional().or(z.literal("")),
  allow_personal_email_login: z.boolean().default(true),
  default_subscription_plan_id: z.string().trim().optional().or(z.literal("")),
  content_font_scale: z
    .union([z.coerce.number().min(0.85).max(1.4), z.literal("")])
    .optional(),
  benefits_enabled: z.boolean().default(false),
  benefits_governance_note: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .or(z.literal("")),
};

export const createCorporateSchema = z.object({
  ...baseShape,
  slug: z
    .string()
    .trim()
    .min(1, "Slug is required")
    .max(64, "Max 64 characters")
    .regex(SLUG_REGEX, "Lowercase letters, numbers, and hyphens only"),
});

// Edit drops slug — slugs are immutable post-create on the backend.
export const updateCorporateSchema = z.object(baseShape);

// Convert form values to the API patch shape. Returns only the keys whose
// values changed compared to `original` (the loaded corporate). Pass
// `original` as null to send everything (used by create).
export const toCorporatePayload = (values, original = null) => {
  const domains = (values.verification_email_domains || [])
    .map((d) => (typeof d === "string" ? d : d?.value))
    .map((d) => (d || "").trim().toLowerCase())
    .filter(Boolean);

  const next = {
    name: values.name,
    display_name: values.display_name || values.name,
    verification_email_domains: domains,
    primary_color: values.primary_color || null,
    secondary_color: values.secondary_color || null,
    industry: values.industry || null,
    timezone: values.timezone || null,
    support_email: values.support_email || null,
    support_phone: values.support_phone || null,
    account_owner_email: values.account_owner_email || null,
    contract_seat_limit:
      values.contract_seat_limit === "" || values.contract_seat_limit == null
        ? 0
        : Number(values.contract_seat_limit),
    contract_start_date: values.contract_start_date || null,
    contract_end_date: values.contract_end_date || null,
    allow_personal_email_login: values.allow_personal_email_login,
    default_subscription_plan_id: values.default_subscription_plan_id || null,
    content_font_scale:
      values.content_font_scale === "" || values.content_font_scale == null
        ? null
        : Number(values.content_font_scale),
    benefits_enabled: !!values.benefits_enabled,
    benefits_governance_note: values.benefits_governance_note || null,
  };

  if (values.slug !== undefined) {
    next.slug = values.slug;
  }

  // The two benefits-master-switch fields don't ship with the standard create
  // flow — they're toggled later via the Benefits tab. Strip them from create
  // payloads so the create endpoint doesn't see unexpected keys.
  if (!original) {
    delete next.benefits_enabled;
    delete next.benefits_governance_note;
  }

  if (!original) return next;

  // Diff: keep only keys that actually changed, comparing primitives by
  // value and arrays by JSON shape (domains).
  const patch = {};
  Object.entries(next).forEach(([key, value]) => {
    const prev = original[key];
    if (Array.isArray(value)) {
      if (JSON.stringify(value) !== JSON.stringify(prev || [])) {
        patch[key] = value;
      }
      return;
    }
    // Treat null and "" as equivalent so we don't churn on empty strings.
    const prevNorm = prev === undefined || prev === "" ? null : prev;
    const nextNorm = value === undefined || value === "" ? null : value;
    if (prevNorm !== nextNorm) {
      patch[key] = value;
    }
  });
  return patch;
};

// ─── Benefits ────────────────────────────────────────────────────────────────

// Mirrors BENEFITS_API.md §2.2. Keep the strings exactly aligned with the
// backend Pydantic enums so the API accepts them verbatim.
export const BENEFIT_CATEGORY_SUGGESTIONS = [
  "caregiving",
  "mental_health",
  "financial_wellness",
  "childcare",
  "legal",
  "physical_wellness",
  "crisis_emergency",
  "career_coaching",
  "behavioral_health",
  "relationship_family",
  "other",
];

export const BENEFIT_DELIVERY_METHODS = ["sms", "email", "link", "in_app"];

export const BENEFIT_FOLLOW_UP_ACTIONS = [
  "send_sms",
  "send_email",
  "open_portal",
  "share_contact",
  "schedule_callback",
];

export const BENEFIT_STATUSES = ["draft", "active", "archived"];

// Admin-authored Jinja2 template fields. Order here is the order the
// service-side validator iterates, but the UI may surface them grouped by
// channel.
export const BENEFIT_TEMPLATE_FIELDS = [
  "sms_template",
  "email_subject_template",
  "email_body_template",
  "email_sendgrid_template_id",
  "inapp_title_template",
  "inapp_body_template",
  "inapp_icon",
];

// Variables admins can reference inside a template. Source of truth:
// core-lambda/admin_module/corporate_management/benefits/template_renderer.py
// → build_render_context(). Keep these in lockstep.
export const BENEFIT_TEMPLATE_VARIABLES = [
  { token: "{{ benefit.name }}", hint: "The benefit's display name." },
  { token: "{{ benefit.provider }}", hint: "Vendor / brand." },
  { token: "{{ benefit.category }}", hint: "Category label." },
  { token: "{{ benefit.helps_with }}", hint: "Plain-English summary." },
  { token: "{{ benefit.portal_url }}", hint: "Member portal URL." },
  { token: "{{ benefit.contact_phone }}", hint: "Contact phone." },
  { token: "{{ benefit.contact_email }}", hint: "Contact email." },
  { token: "{{ corporate.display_name }}", hint: "Your organization's display name." },
  { token: "{{ member.first_name }}", hint: "First name of the member receiving the message." },
  { token: "{{ note }}", hint: "Optional free-form note attached by ARIS." },
];

const optionalUrl = z
  .string()
  .trim()
  .url("Use a full URL like https://example.com/care")
  .refine((v) => !v || v.startsWith("https://"), "Must start with https://")
  .optional()
  .or(z.literal(""));

export const createBenefitSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  provider: z.string().trim().min(1, "Provider is required").max(200),
  category: z.string().trim().min(1, "Category is required").max(80),
  helps_with: z
    .string()
    .trim()
    .min(1, "Tell the AI what this benefit helps with")
    .max(2000),
  suggested_intro: z
    .string()
    .trim()
    .min(1, "Required — used by the AI as a tone anchor")
    .max(2000),
  signal_value_ids: z.array(z.string()).default([]),
  signal_hints: z.array(z.string()).max(20).default([]),
  follow_up_actions: z
    .array(z.enum(BENEFIT_FOLLOW_UP_ACTIONS))
    .default([]),
  delivery_methods: z.array(z.enum(BENEFIT_DELIVERY_METHODS)).default([]),
  portal_url: optionalUrl,
  contact_phone: z.string().trim().max(64).optional().or(z.literal("")),
  contact_email: optionalEmail,
  info_sheet_s3_key: z.string().trim().max(500).optional().or(z.literal("")),
  priority: z.coerce.number().int().min(0).default(100),
  cooldown_days: z.coerce.number().int().min(0).default(14),
  crisis_only: z.boolean().default(false),
  // Delivery copy (Jinja2 templates). All optional — server falls back to
  // auto-composer when blank.
  sms_template: z
    .string()
    .trim()
    .max(600, "Max 600 characters")
    .optional()
    .or(z.literal("")),
  email_subject_template: z
    .string()
    .trim()
    .max(300, "Max 300 characters")
    .optional()
    .or(z.literal("")),
  email_body_template: z
    .string()
    .max(10000, "Max 10000 characters")
    .optional()
    .or(z.literal("")),
  email_body_is_html: z.boolean().default(false),
  email_sendgrid_template_id: z
    .string()
    .trim()
    .max(64, "Max 64 characters")
    .optional()
    .or(z.literal("")),
  inapp_title_template: z
    .string()
    .trim()
    .max(200, "Max 200 characters")
    .optional()
    .or(z.literal("")),
  inapp_body_template: z
    .string()
    .max(2000, "Max 2000 characters")
    .optional()
    .or(z.literal("")),
  inapp_icon: z
    .string()
    .trim()
    .max(80, "Max 80 characters")
    .optional()
    .or(z.literal("")),
});

// Same shape, every key optional — drives PATCH /benefits/{id}.
export const updateBenefitSchema = createBenefitSchema.partial();

// PUT /benefits/enablements/{global_benefit_id}: only the 5 fields below are
// overridable. Anything else on the underlying global is intentionally locked.
export const globalEnablementOverridesSchema = z.object({
  override_portal_url: optionalUrl.nullable(),
  override_contact_phone: z
    .string()
    .trim()
    .max(64)
    .optional()
    .or(z.literal(""))
    .nullable(),
  override_contact_email: z
    .string()
    .trim()
    .email("Invalid email")
    .optional()
    .or(z.literal(""))
    .nullable(),
  override_suggested_intro: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .or(z.literal(""))
    .nullable(),
  override_priority: z
    .union([z.coerce.number().int().min(0), z.literal("")])
    .optional()
    .nullable(),
});

// Normalize the form values into the backend payload shape. Empty strings
// become null so the backend treats them as "clear this field".
const normalizeNullable = (v) =>
  v === "" || v === undefined ? null : v;

export const toBenefitPayload = (values, original = null) => {
  const next = {
    name: values.name,
    provider: values.provider,
    category: values.category,
    helps_with: values.helps_with,
    suggested_intro: values.suggested_intro,
    signal_value_ids: Array.isArray(values.signal_value_ids)
      ? values.signal_value_ids
      : [],
    signal_hints: Array.isArray(values.signal_hints) ? values.signal_hints : [],
    follow_up_actions: Array.isArray(values.follow_up_actions)
      ? values.follow_up_actions
      : [],
    delivery_methods: Array.isArray(values.delivery_methods)
      ? values.delivery_methods
      : [],
    portal_url: normalizeNullable(values.portal_url),
    contact_phone: normalizeNullable(values.contact_phone),
    contact_email: normalizeNullable(values.contact_email),
    info_sheet_s3_key: normalizeNullable(values.info_sheet_s3_key),
    priority:
      values.priority === "" || values.priority == null
        ? 100
        : Number(values.priority),
    cooldown_days:
      values.cooldown_days === "" || values.cooldown_days == null
        ? 14
        : Number(values.cooldown_days),
    crisis_only: !!values.crisis_only,
    sms_template: normalizeNullable(values.sms_template),
    email_subject_template: normalizeNullable(values.email_subject_template),
    email_body_template: normalizeNullable(values.email_body_template),
    email_body_is_html: !!values.email_body_is_html,
    email_sendgrid_template_id: normalizeNullable(
      values.email_sendgrid_template_id
    ),
    inapp_title_template: normalizeNullable(values.inapp_title_template),
    inapp_body_template: normalizeNullable(values.inapp_body_template),
    inapp_icon: normalizeNullable(values.inapp_icon),
  };

  if (!original) return next;

  const patch = {};
  Object.entries(next).forEach(([key, value]) => {
    const prev = original[key];
    if (Array.isArray(value)) {
      if (JSON.stringify(value) !== JSON.stringify(prev || [])) {
        patch[key] = value;
      }
      return;
    }
    const prevNorm = prev === undefined || prev === "" ? null : prev;
    const nextNorm = value === undefined || value === "" ? null : value;
    if (prevNorm !== nextNorm) {
      patch[key] = value;
    }
  });
  return patch;
};

// Mirror behavior for the 5-field overrides PUT. Pass null explicitly to
// clear; pass undefined to leave the existing override untouched.
export const toEnablementOverridesPayload = (values) => ({
  override_portal_url: normalizeNullable(values.override_portal_url),
  override_contact_phone: normalizeNullable(values.override_contact_phone),
  override_contact_email: normalizeNullable(values.override_contact_email),
  override_suggested_intro: normalizeNullable(values.override_suggested_intro),
  override_priority:
    values.override_priority === "" ||
    values.override_priority === null ||
    values.override_priority === undefined
      ? null
      : Number(values.override_priority),
});

// Convert a hydrated BenefitResponse back into form values (used when editing).
export const benefitToFormValues = (b) => ({
  name: b?.name || "",
  provider: b?.provider || "",
  category: b?.category || "",
  helps_with: b?.helps_with || "",
  suggested_intro: b?.suggested_intro || "",
  signal_value_ids: Array.isArray(b?.signal_value_ids) ? b.signal_value_ids : [],
  signal_hints: Array.isArray(b?.signal_hints) ? b.signal_hints : [],
  follow_up_actions: Array.isArray(b?.follow_up_actions)
    ? b.follow_up_actions
    : [],
  delivery_methods: Array.isArray(b?.delivery_methods)
    ? b.delivery_methods
    : [],
  portal_url: b?.portal_url || "",
  contact_phone: b?.contact_phone || "",
  contact_email: b?.contact_email || "",
  info_sheet_s3_key: b?.info_sheet_s3_key || "",
  priority: b?.priority ?? 100,
  cooldown_days: b?.cooldown_days ?? 14,
  crisis_only: !!b?.crisis_only,
  sms_template: b?.sms_template || "",
  email_subject_template: b?.email_subject_template || "",
  email_body_template: b?.email_body_template || "",
  email_body_is_html: !!b?.email_body_is_html,
  email_sendgrid_template_id: b?.email_sendgrid_template_id || "",
  inapp_title_template: b?.inapp_title_template || "",
  inapp_body_template: b?.inapp_body_template || "",
  inapp_icon: b?.inapp_icon || "",
});

export const enablementToFormValues = (en) => ({
  override_portal_url: en?.override_portal_url || "",
  override_contact_phone: en?.override_contact_phone || "",
  override_contact_email: en?.override_contact_email || "",
  override_suggested_intro: en?.override_suggested_intro || "",
  override_priority:
    en?.override_priority === null || en?.override_priority === undefined
      ? ""
      : en.override_priority,
});
